import {
    CircleSponsorshipEligibilityResponse,
    CircleSponsorshipUpsertRequest,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useMutation, useQuery } from '@rhg/libs/react-query';
import { getRhgAppAppendHeader } from '@rhg/utils/browser';

export const partnerEligibilityQueryOptions = {
    queryKey: function (
        partnerId?: string,
        request?: CircleSponsorshipUpsertRequest
    ) {
        return [queryKeys.partnerEligibility, partnerId, request];
    },
    queryFn: async function ({
        getAccessTokenSilently,
        partnerId,
        request,
        isSSO = false,
        isImplied = false,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        partnerId: string;
        request: CircleSponsorshipUpsertRequest;
        isSSO?: boolean;
        isImplied?: boolean;
    }): Promise<CircleSponsorshipEligibilityResponse | null> {
        const circleApi = createCircleApi(getAccessTokenSilently);

        const response = await circleApi
            .v1ValidatePartnerEligibility(
                {
                    partnerId,
                    circleSponsorshipEligibilityRequest: request,
                    implied: isImplied,
                },
                {
                    headers: getRhgAppAppendHeader({
                        registration_flow: isSSO ? 'sso' : 'direct',
                    }),
                }
            )
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    return { data: null };
                } else {
                    throw e;
                }
            });

        return response?.data;
    },
    queryOptions: function (
        isAuthenticated: boolean,
        partnerId?: string,
        request?: CircleSponsorshipUpsertRequest
    ) {
        return {
            enabled: !!(isAuthenticated && partnerId && request),
            retry: false,
            staleTime: 1000 * 60 * 15, // 15 minutes
        };
    },
};

export function usePartnerEligibility(
    partnerId?: string,
    request?: CircleSponsorshipUpsertRequest,
    isSSO = false,
    isImplied = false
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn, queryOptions } = partnerEligibilityQueryOptions;

    const options = queryOptions(isAuthenticated, partnerId, request);

    const { data, ...otherProps } = useQuery<
        CircleSponsorshipEligibilityResponse | null | undefined
    >({
        queryKey: queryKey(partnerId, request),
        queryFn: async () => {
            if (!request) return;
            return queryFn({
                getAccessTokenSilently,
                partnerId: partnerId!,
                request,
                isSSO,
                isImplied,
            });
        },
        ...options,
        // blockRefetches
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        eligibility: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useGetPartnerEligibility() {
    const { getAccessTokenSilently } = useAuth();
    const { queryFn } = partnerEligibilityQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            partnerId,
            request,
            isSSO,
            isImplied,
        }: {
            partnerId: string;
            request: CircleSponsorshipUpsertRequest;
            isSSO?: boolean;
            isImplied?: boolean;
        }) => {
            return queryFn({
                getAccessTokenSilently,
                partnerId,
                request,
                isSSO,
                isImplied,
            });
        },
    });

    return {
        getPartnerEligibility: mutate,
        getPartnerEligibilityAsync: mutateAsync,
        ...otherProps,
    };
}
